import React, { Component } from 'react';
import config from 'react-global-configuration';
import { Formik, setNestedObjectValues } from 'formik';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import c from 'classnames';

import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import WebformMapper from '@uncinc/react-drupal-core/dist/Components/Webform/WebformMapper';
import { FormikExtendedContextProvider } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

class WebformComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      submitted: false,
      message: '',
      currentPage: 0,
    };
  }

  /**
   * @description Scroll to the top of the webform component
   *
   * @returns {void}
   */
  scrollToTop = () => {
    window.scrollTo(0, 0);
    document.body.setAttribute('tabindex', '-1');
    document.body.focus();

    document.body.addEventListener('blur', this.handleBodyBlur);
  };

  handleBodyBlur = () => {
    document.body.removeAttribute('tabindex');

    document.body.removeEventListener('blur', this.handleBodyBlur);
  };

  componentWillUnmount() {
    document.body.removeEventListener('blur', this.handleBodyBlur);
  }

  onNextPage = () => {
    const { currentPage } = this.state;
    this.setState({
      currentPage: currentPage + 1,
    }, () => {
      if (this.props.onNextMultiFormStep) {
        this.props.onNextMultiFormStep();
      }
    });
  };

  onPreviousPage = () => {
    const { currentPage } = this.state;
    this.setState({
      currentPage: currentPage - 1,
    }, () => {
      if (this.props.onPrevMultiFormStep) {
        this.props.onPrevMultiFormStep();
      }
    });
  };

  collectPages = (entities) => {
    let output = new Immutable.List();
    entities.entrySeq().forEach(([key, entity]) => {
      const mappedEntity = Immutable.fromJS(entity);
      if (mappedEntity.get('#type') === 'webform_wizard_page') {
        output = output.push(mappedEntity.get('#webform_key'));
      }
    });

    return output;
  };

  /**
   * @description Determines the visibility based in Drupal #states.
   *
   * @param {*} entities
   *   The entities to display
   * @param {*} values
   *   The values to submit
   *
   * @return {boolean}
   *   If the field should be visible
   */
  determineVisibility = (entities, values) => {
    let output = new Immutable.OrderedMap();

    const determineVisibilityForElement = (state) => {
      const visible = state.keySeq().every((component) => {
        const condition = state.get(component);
        if (!condition) {
          return false;
        }
        const fieldName = component.match(/input\[name="(.+)"\]/)[1];

        // Determine the type of condition.
        if (condition.get('filled')) {
          if (values[fieldName]) {
            return true;
          }
        } else if (condition.get('empty')) {
          if (!values[fieldName]) {
            return true;
          }
        } else if (condition.get('value')) {
          const value = condition.get('value');
          // check if is object
          if (value && typeof value === 'object' && value.get('between') && values[fieldName]) {
            // check if 'between'
            const betweenValues = value.get('between').split(':');
            if (betweenValues.length !== 2) {
              console.log('The \'between\' condition supports two values divided by colon only.');
            } else if (Number(values[fieldName]) >= Number(betweenValues[0]) && Number(values[fieldName]) <= Number(betweenValues[1])) {
              // if value between the condition
              return true;
            }
          }
          // Check for subfield.
          const matches = [...fieldName.matchAll(/(.*)\[(.*)\]/g)];
          if (matches && matches.length > 0) {
            const [, field] = matches[0];
            if (values[field]) {
              if (values[field].includes(value)) {
                return true;
              }
            }
          }
          if (values[fieldName] === value) {
            return true;
          }
        } else if (condition.get('checked')) {
          // Check for subfield.
          const matches = [...fieldName.matchAll(/(.*)\[(.*)\]/g)];
          if (matches && matches.length > 0) {
            const [, field, subField] = matches[0];
            if (values[field]) {
              if (values[field].includes(subField)) {
                return true;
              }
            }
          }
          if (values[fieldName] === true) {
            return true;
          }
        } else {
          console.log('Condition not yet supported:', condition);
        }

        // By default, states will make a field invisible.
        return false;
      });

      return visible;
    };

    entities.entrySeq().forEach(([key, entity]) => {
      if (key.indexOf('#') === 0) {
        return;
      }

      let visible = true;

      if (entity.hasIn(['#states', 'visible']) || entity.hasIn(['#states', 'invisible'])) {
        const state = entity.hasIn(['#states', 'visible']) ? entity.getIn(['#states', 'visible']) : entity.getIn(['#states', 'invisible']);

        if (Immutable.Map.isMap(state)) {
          // State is a Map, which means a single condition.
          visible = determineVisibilityForElement(state);
        } else if (Immutable.List.isList(state)) {
          // State is a List, which means multiple conditions, separated by an operator (and, or, xor).
          let operator = 'or';
          visible = determineVisibilityForElement(state.get(0));
          state.slice(1).forEach((substate) => {
            if (typeof substate === 'string') {
              operator = substate;
            } else if (Immutable.Map.isMap(substate)) {
              switch (operator) {
                case 'and':
                  visible = visible && determineVisibilityForElement(substate);
                  break;

                case 'or':
                  visible = visible || determineVisibilityForElement(substate);
                  break;

                case 'xor':
                  visible = visible ^ determineVisibilityForElement(substate);
                  break;

                default:
                  break;
              }
            }
          });
        }
      }

      // Flip the condition if the state is 'invisible'.
      if (entity.hasIn(['#states', 'invisible'])) {
        visible = !visible;
      }

      entity = entity.set('#visible', visible);

      // Recursively loop over children.
      let children = entity;
      children.keySeq().forEach((k) => {
        if (k.indexOf('#') === 0) {
          children = children.delete(k);
        }
      });
      children = this.determineVisibility(children, values);

      children.keySeq().forEach((v) => {
        entity = entity.set(v, children.get(v));
      });
      entity = entity.merge(children);

      output = output.set(key, entity);
    });

    return output;
  };

  collectInitialValuesFlat = (entities) => {
    let output = new Immutable.OrderedMap();
    entities.entrySeq().forEach(([key, entity]) => {
      if (key.indexOf('#') === 0) {
        return;
      }

      const mappedEntity = Immutable.fromJS(entity);
      const defaultValueFromDrupal = mappedEntity.get('#default_value');

      let defaultValue;

      switch (mappedEntity.get('#type')) {
        case 'webform_entity_checkboxes':
        case 'webform_checkboxes_other':
        case 'webform_checkboxes':
        case 'checkboxes':
        case 'checkboxes_other':
        case 'entity_checkboxes':
          defaultValue = [];
          break;

        default:
          defaultValue = defaultValueFromDrupal || '';
      }

      // Currently only used for text fields with delta
      if (entity.get('#multiple') && entity.get('#type') === 'textfield') {
        defaultValue = [];
      }

      output = output.set(mappedEntity.get('#webform_key'), defaultValue);

      // Collect child items.
      let children = Immutable.fromJS(entity);
      children.keySeq().forEach((k) => {
        if (k.indexOf('#') === 0) {
          children = children.delete(k);
        }
      });

      // Iterate over children and merge them.
      const c = this.collectInitialValuesFlat(children);
      output = output.merge(c);
    });

    return output;
  };

  handleSuccessfulSubmit(data) {
    if (data.confirmation) {
      switch (data.confirmation.type) {
        case 'url': {
          window.location.href = data.confirmation.url;
          break;
        }
        case 'message': {
          this.setState({ submitted: true, message: data.confirmation.message });
          break;
        }
        case 'none':
        default: {
          break;
        }
      }
    } else {
      this.setState({ submitted: true });
    }

    // Run custom function if it exists. Include submission response.
    this.props.onSuccessfulSubmit(data);

    if (this.props.scrollToTopAfterSubmit) {
      this.scrollToTop();
    }
  }

  handleFailedSubmit(error) {
    this.setState({
      submitted: false,
      error: true,
    });
    if (this.props.onFailedSubmit) {
      this.props.onFailedSubmit(error);
    }
  }

  hasWebformActions = (elements) => {
    let objectFound = false;
    // Check on type 'webform_actions'
    elements.entrySeq().forEach(([key, element]) => {
      if (key.indexOf('#') === 0) {
        return;
      }

      const entity = Immutable.fromJS(element);
      if (entity.get('#type') === 'webform_actions') {
        objectFound = true;
      }
      // Recursively loop over children if we have not found.
      if (!objectFound) {
        let children = entity;
        children.keySeq().forEach((k) => {
          if (k.indexOf('#') === 0) {
            children = children.delete(k);
          }
        });
        objectFound = this.hasWebformActions(children);
      }
    });
    return objectFound;
  };

  /**
   * Handles the submission of the form.
   *
   * @param {*} values
   *   The values to submit
   * @param {*} handleResponse
   *   Boolean indicating if we should have the response, e.g. display a message.
   *   Useful for auto-submission purposes.
   *
   * @returns {React.FC}
   *   Some jsx
   */
  async submit(values, handleResponse = true) {
    const { id, language, onSubmit, method = 'post', uuid } = this.props;
    const API_URL = config.get('API_URL');
    const MULTILINGUAL = config.get('MULTILINGUAL', true);
    const PATH = MULTILINGUAL ? `${API_URL}/${language}` : API_URL;

    const uppercaseMethod = method.toUpperCase();

    const url = uppercaseMethod === 'POST' ? `${PATH}/webform_rest/submit` : `${PATH}/webform_rest/${id}/submission/${uuid}`;

    // Replace the 'other' options with the actual value.
    Object.keys(values).forEach((key) => {
      if (values[key] === '__other__') {
        values[key] = values[`${key}--other`];
      }
    });

    if (typeof onSubmit === 'function') {
      onSubmit(values);
    }

    let csrfToken;
    await fetch(`${PATH}/rest/session/token`, { credentials: 'include' })
      .then((response) => response.text())
      .then((response) => {
        csrfToken = response;
        fetch(url, {
          method: uppercaseMethod,
          credentials: 'include',
          body: JSON.stringify({
            webform_id: id,
            ...values,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        }).then((response) => response.json())
          .then((response) => {
            if (response.error) {
              this.handleFailedSubmit(response.error);
            } else if (handleResponse) {
              this.handleSuccessfulSubmit(response);
            }
          })
          .catch((e) => {
            this.handleFailedSubmit(e);
          });
      })
      .catch((e) => {
        this.handleFailedSubmit(e);
      });
  }

  render() {
    const { id, entities, title, overriddenComponents, status, info, initialValues, displayValidationErrorBottom } = this.props;
    const { error, submitted, message, currentPage } = this.state;
    const pages = this.collectPages(entities);
    const initialDrupalValues = this.collectInitialValuesFlat(entities).toJS();
    // Merge the initial values into the Drupal values.
    const mergedValues = { ...initialDrupalValues, ...initialValues };

    if ((status && status === 'closed') && info) {
      const closedMessage = info.getIn(['settings', 'form_close_message'], 'Dit formulier is gesloten.');
      return (
        <div className="WebformComponent__closed">
          <WysiwygField
            property="schema:text"
            html={closedMessage}
            className="WebformAddressAutocomplete__description WebformComponent--description" />
        </div>
      );
    } else if (submitted === true) {
      return (
        <div className="WebformComponent__submitted">
          <WysiwygField
            property="schema:text"
            html={message}
            className="WebformAddressAutocomplete__description WebformComponent--description" />
        </div>
      );
    } else {
      const webformActions = this.hasWebformActions(entities);
      const webformWrapperClasses = c(Immutable.fromJS(entities).getIn(['#attributes', 'class'], Immutable.List()).toJS());
      return (
        <div className={webformWrapperClasses}>
          <Formik
            initialValues={mergedValues}
            onSubmit={(values, actions) => this.submit(values)}
            render={(props) => {
              // Determine visibility.
              let entitiesMapped = this.determineVisibility(entities, props.values);

              if (!webformActions) {
                // No actions defined, so add them client-side.
                entitiesMapped = entitiesMapped.set('actions', Immutable.Map({
                  '#type': 'webform_actions',
                  '#submit__label': 'Versturen',
                  '#webform': id,
                  '#webform_id': `${id}--actions`,
                  '#webform_key': 'actions',
                  '#webform_plugin_id': 'webform_actions',
                }));
              }

              return (
                <FormikExtendedContextProvider>
                  {title && (
                    <div className="WebformParagraph__title-wrapper">
                      <h3 className="WebformParagraph__title">{title}</h3>
                    </div>
                  )}
                  <div className="WebformComponent__form">
                    <WebformMapper
                      {...props}
                      {...this.props}
                      id={id}
                      entities={entitiesMapped}
                      overriddenComponents={overriddenComponents}
                      pages={pages}
                      onNextPage={this.onNextPage}
                      onPreviousPage={this.onPreviousPage}
                      currentPage={pages.get(currentPage)}
                      handleSubmit={async (handleResponse) => {
                        // Validate the form, and block submissions if not valid. We need to somehow
                        // manually trigger the touched event for all fields, otherwise the validation
                        // messages will not show.
                        // @see https://github.com/jaredpalmer/formik/issues/2734
                        const validationErrors = await props.validateForm();
                        if (Object.keys(validationErrors).length > 0) {
                          props.setTouched(setNestedObjectValues(validationErrors, true));
                          return;
                        }

                        props.setSubmitting(true);
                        await this.submit(props.values, handleResponse);
                        props.setSubmitting(false);
                      }}
                    />
                    { error && <div className="WebformComponent_error WebformComponent--error">{ this.props.t(['webform.general_error', 'webform_general_error']) }</div>}
                    { displayValidationErrorBottom && (Object.keys(props.errors).length !== 0 && displayValidationErrorBottom)}
                  </div>
                </FormikExtendedContextProvider>
              );
            }}
          />
        </div>
      );
    }
  }
}

WebformComponent.propTypes = {
  displayValidationErrorBottom: PropTypes.element,
  scrollToTopAfterSubmit: PropTypes.bool,
  scrollToTopAfterMultiFormStep: PropTypes.bool,
  onSuccessfulSubmit: PropTypes.func,
  onFailedSubmit: PropTypes.func,
  initialValues: PropTypes.objectOf(PropTypes.string),
  onSubmit: PropTypes.func,
  t: PropTypes.func,
  onNextMultiFormStep: PropTypes.func,
  onPrevMultiFormStep: PropTypes.func,
};

WebformComponent.defaultProps = {
  t: null,
  displayValidationErrorBottom: null,
  scrollToTopAfterSubmit: true,
  scrollToTopAfterMultiFormStep: true,
  onSuccessfulSubmit: (data) => {},
  onFailedSubmit: (data) => {},
  initialValues: {},
  onSubmit: () => {},
  onNextMultiFormStep: () => {},
  onPrevMultiFormStep: () => {},
};

// TOOD: don't use webformState
function mapStateToProps(state, ownProps) {
  if (!state) {
    return {};
  }

  return {
    language: state.getIn(['ui', 'language']),
    webformState: state.getIn(['ui', 'webformState']),
  };
}

const WebformComponentTranslated = withTranslation()(WebformComponent);
export default connect(mapStateToProps)(WebformComponentTranslated);
